import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/useAuth";

type LandingNavKey = "overview" | "clients" | "temperature" | "analytics" | "configuration" | "admin";

type LandingShellProps = {
  active: LandingNavKey;
  breadcrumb: string;
  searchPlaceholder?: string;
  contentClassName?: string;
  children: ReactNode;
};

// SVG Icons
const OverviewIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
);

const ClientsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const TemperatureIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"></path>
  </svg>
);

const AnalyticsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="20" x2="12" y2="10"></line>
    <line x1="18" y1="20" x2="18" y2="4"></line>
    <line x1="6" y1="20" x2="6" y2="16"></line>
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M12 1v6m0 6v6m8.66-9l-5.2 3m-6.92 4l-5.2 3M20.66 17l-5.2-3m-6.92-4l-5.2-3"></path>
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

export function LandingShell({
  active,
  breadcrumb,
  searchPlaceholder = "Rechercher...",
  contentClassName = "",
  children,
}: LandingShellProps) {
  const { logout, user } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navItemClass = (key: LandingNavKey) =>
    `sidebar-nav-item${active === key ? " active" : ""}`;

  async function handleLogout() {
    setIsLoggingOut(true);

    try {
      await logout();
    } catch {
      setIsLoggingOut(false);
    }
  }

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className="app-sidebar">
        {/* Brand */}
        <div className="sidebar-brand">
          <div className="brand-logo">
            <div className="brand-logo-text">Lainvision</div>
            {/* Decorative particle */}
            <div className="brand-particle-accent"></div>
          </div>
          <p className="brand-tagline">Gestion de données clients</p>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="sidebar-nav-section">
            <div className="sidebar-nav-title">Navigation</div>
            
            <Link to="/" className={navItemClass("overview")}>
              <span className="nav-icon">
                <OverviewIcon />
              </span>
              <span className="nav-label">Vue d'ensemble</span>
            </Link>

            <Link to="/clients" className={navItemClass("clients")}>
              <span className="nav-icon">
                <ClientsIcon />
              </span>
              <span className="nav-label">Clients</span>
              <span className="nav-badge">3</span>
            </Link>

            <Link to="/temperature" className={navItemClass("temperature")}>
              <span className="nav-icon">
                <TemperatureIcon />
              </span>
              <span className="nav-label">Température</span>
            </Link>
          </div>

          <div className="sidebar-nav-section">
            <div className="sidebar-nav-title">Analytics</div>
            
            <Link to="/clients" className={navItemClass("analytics")}>
              <span className="nav-icon">
                <AnalyticsIcon />
              </span>
              <span className="nav-label">Analytics</span>
            </Link>
          </div>

          <div className="sidebar-nav-section sidebar-nav-bottom">
            <div className="sidebar-nav-title">Paramètres</div>
            
            <Link to="/configuration" className={navItemClass("configuration")}>
              <span className="nav-icon">
                <SettingsIcon />
              </span>
              <span className="nav-label">Configuration</span>
            </Link>

            {user?.roles.includes("datavis_admin") ? (
              <Link to="/admin/access" className={navItemClass("admin")}>
                <span className="nav-icon">
                  <SettingsIcon />
                </span>
                <span className="nav-label">Admin</span>
              </Link>
            ) : null}
          </div>
        </nav>

        {/* Decorative brand element at bottom */}
        <div className="sidebar-decoration"></div>
      </aside>

      {/* Main Content Area */}
      <main className="app-main">
        {/* Top Bar */}
        <header className="app-topbar">
          <div className="topbar-breadcrumb">
            <h1 className="page-title-topbar">{breadcrumb}</h1>
          </div>
          
          <div className="topbar-actions">
            <div className="search-box">
              <SearchIcon />
              <input
                type="search"
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="search-input"
              />
            </div>
            <button
              type="button"
              className="logout-button"
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? "Déconnexion…" : "Se déconnecter"}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className={`app-content ${contentClassName}`.trim()}>
          {children}
        </div>
      </main>
    </div>
  );
}
